import { useMemo } from "react";
import { useAuthHeader } from "react-auth-kit";
import { useApi } from "../home/apiService";

const apiUrl =
  process.env.REACT_APP_ENV === "LOCAL"
    ? "http://localhost:5000"
    : "https://api.spotprisprognos.se:8443";

export interface ForumCategory {
  slug: string;
  nameSv: string;
  nameEn: string;
  descriptionSv: string | null;
  descriptionEn: string | null;
  systemOnly: boolean;
  color: string | null;
  icon: string | null;
  threadCount: number;
  lastPostAt: string | null;
}

export interface ForumThreadSummary {
  id: string;
  title: string;
  kind: "user" | "daily";
  dailyDate: string | null;
  category: string;
  authorName: string | null;
  isPinned: boolean;
  isLocked: boolean;
  isHidden: boolean;
  createdAt: string;
  lastPostAt: string;
  replyCount: number;
  viewCount: number;
  score: number;
  excerpt: string | null;
  participants: string[];
}

export type ThreadSort = "active" | "new" | "top";

export interface ForumStats {
  threads: number;
  posts: number;
  members: number;
  postsToday: number;
  views: number;
  topMembers: { name: string; posts: number; votes: number }[];
}

export interface DailyAreaDay {
  hourly: (number | null)[];
  avg: number;
  min: number;
  max: number;
  minHour: number;
  maxHour: number;
  cheapest3h: { start: number; avg: number } | null;
  forecastAvg: number | null;
}

export interface DailyThreadData {
  version: 1 | 2;
  date: string;
  unit: "SEK/kWh";
  pricesFinal: boolean;
  areas: string[];
  day: Record<string, DailyAreaDay>;
  // The forecast for the days after `date`; `day` (hourly detail) from version 2.
  outlook: { date: string; values: Record<string, number>; day?: Record<string, DailyAreaDay> }[];
  generatedAt: string;
}

export interface ForumPost {
  id: string;
  authorName: string | null;
  authorPosts: number | null;
  isSystem: boolean;
  isModerator: boolean;
  isOriginalPoster: boolean;
  body: string | null;
  status: "ok" | "deleted" | "hidden";
  createdAt: string;
  edited: boolean;
  editedAt: string | null;
  revisions?: number;
  replyTo: { id: string; authorName: string | null } | null;
  score: number;
  myVote: boolean;
  mine: boolean;
  // Moderators only.
  authorId?: string | null;
  authorBanned?: boolean;
  openReports?: number;
}

export interface ForumThreadDetail {
  thread: ForumThreadSummary & { data: DailyThreadData | null };
  posts: ForumPost[];
  canModerate: boolean;
}

export interface ForumMe {
  forumName: string | null;
  nameLocked: boolean; // fixed once the member has posted
  banned: boolean;
  canModerate: boolean;
}

// Moderator overview (admin panel). Items link to the post in the forum.
export interface ModPostRef {
  postId: string;
  threadId: string;
  threadTitle: string;
  authorName: string;
  body: string;
  createdAt: string;
}

export interface ModOverview {
  counts: {
    openReports: number;
    hiddenPosts: number;
    hiddenThreads: number;
    banned: number;
    postsDay: number;
    postsWeek: number;
    threadsWeek: number;
    newMembersWeek: number;
  };
  reports: (ModPostRef & { reports: number; reasons: string[]; lastReport: string; hidden: boolean })[];
  hidden: (ModPostRef & { hiddenAt: string; hiddenBy: string | null })[];
  banned: { name: string | null; bannedAt: string; posts: number; postId: string | null; threadId: string | null }[];
  edited: (ModPostRef & { editedAt: string; revisions: number })[];
}

// A rejected forum write, with the server's error code (e.g. ForumNameRequired).
export class ForumError extends Error {
  code: string;
  constructor(code: string, message?: string) {
    super(message || code);
    this.code = code;
  }
}

// Forum reads are public, but sending the token marks the caller's own posts
// and lets moderators see hidden ones. Writes go through useApi's request path
// (token refresh), which resolves 4xx bodies instead of throwing.
export const useForumApi = () => {
  const authHeader = useAuthHeader();
  const api = useApi();

  return useMemo(() => {
    const get = async <T,>(path: string): Promise<T> => {
      const auth = authHeader();
      const res = await fetch(`${apiUrl}/api/forum${path}`, {
        headers: auth ? { Authorization: auth } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new ForumError(data.error || `HTTP ${res.status}`, data.message);
      return data as T;
    };
    const write = async <T,>(method: string, path: string, body?: unknown): Promise<T> => {
      const data = await api._request(method, `/forum${path}`, body);
      if (data && typeof data === "object" && "error" in data) {
        throw new ForumError(String(data.error), data.message);
      }
      return data as T;
    };

    return {
      categories: () => get<{ categories: ForumCategory[] }>("/categories"),
      threads: (opts: { category?: string | null; sort?: ThreadSort; q?: string; page?: number } = {}) => {
        const p = new URLSearchParams();
        if (opts.category) p.set("category", opts.category);
        if (opts.sort) p.set("sort", opts.sort);
        if (opts.q) p.set("q", opts.q);
        if (opts.page) p.set("page", String(opts.page));
        return get<{ threads: ForumThreadSummary[]; hasMore: boolean }>(`/threads?${p.toString()}`);
      },
      stats: () => get<ForumStats>("/stats"),
      latestDaily: () =>
        get<{ thread: (ForumThreadSummary & { data: DailyThreadData | null }) | null }>("/daily/latest"),
      thread: (id: string) => get<ForumThreadDetail>(`/threads/${id}`),
      // Page-view beacon; fire and forget (the server dedupes per visitor).
      view: (id: string) => {
        fetch(`${apiUrl}/api/forum/threads/${id}/view`, { method: "POST", keepalive: true }).catch(() => undefined);
      },

      me: () => write<ForumMe>("GET", "/me"),
      setName: (forumName: string) => write<{ forumName: string }>("PUT", "/me", { forumName }),
      createThread: (category: string, title: string, body: string) =>
        write<{ id: string }>("POST", "/threads", { category, title, body }),
      reply: (threadId: string, body: string, replyTo?: string | null) =>
        write<{ id: string }>("POST", `/threads/${threadId}/posts`, { body, replyTo: replyTo ?? undefined }),
      vote: (id: string, up: boolean) => write<{ score: number; myVote: boolean }>("POST", `/posts/${id}/vote`, { up }),
      editPost: (id: string, body: string) => write<{ ok: true }>("PUT", `/posts/${id}`, { body }),
      deletePost: (id: string) => write<{ ok: true }>("DELETE", `/posts/${id}`),
      report: (id: string, reason: string) =>
        write<{ ok: true; alreadyReported: boolean }>("POST", `/posts/${id}/report`, { reason }),

      modOverview: () => write<ModOverview>("GET", "/mod/overview"),
      modPostReports: (id: string) =>
        write<{ reports: { reason: string | null; createdAt: string; reporter: string | null }[] }>(
          "GET",
          `/mod/posts/${id}/reports`,
        ),
      modResolveReports: (id: string) => write<{ ok: true; resolved: number }>("POST", `/mod/posts/${id}/resolve-reports`, {}),
      modHidePost: (id: string, hidden: boolean) => write<{ ok: true }>("PUT", `/mod/posts/${id}`, { hidden }),
      modRevisions: (id: string) =>
        write<{ revisions: { body: string; replacedAt: string }[] }>("GET", `/mod/posts/${id}/revisions`),
      modThread: (
        id: string,
        patch: { isPinned?: boolean; isLocked?: boolean; isHidden?: boolean; title?: string; category?: string },
      ) =>
        write<{ ok: true }>("PUT", `/mod/threads/${id}`, patch),
      modBan: (accountId: string, banned: boolean) =>
        write<{ ok: true }>("PUT", `/mod/accounts/${accountId}`, { banned }),
      modRename: (accountId: string, forumName: string) =>
        write<{ ok: true }>("PUT", `/mod/accounts/${accountId}`, { forumName }),
      modDaily: (rebuild = false) =>
        write<{ status: string; date: string; threadId?: string }>("POST", "/mod/daily", { rebuild }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
